import React, { useMemo } from "react";
import * as d3 from "d3";
import {
  feeling_config,
  task_completion_config,
  task_completion_rate_config,
  wave_config,
  feeling_doing_config,
  morning_evening_config,
  doing_shift_config,
  carryover_config,
  moment_mix_config,
} from "../features/charts/configs/graphconfig";
import { GraphWrapper } from "./GraphWrapper";
import ChartHeader from "./ChartHeader/ChartHeader";

function GraphRenderer({
  title,
  data,
  family,
  viewMode,
  graphSelected,
  onNodeSelect,
  onNodeHover,
  onViewStateChange,
  viewOffset,
  setViewOffset,
  selectedId,
  average,
  picker,
}) {
  const parseDate = d3.timeParse("%Y-%m-%d");

  // 2. Memoize Data Processing
  // This logic now only runs when 'data' changes, improving performance.
  const processedData = useMemo(() => {
    if (!data) return {};

    // Sort once to avoid sorting multiple times for different datasets
    const sortedData = [...data].sort(
      (a, b) => new Date(a.logDate) - new Date(b.logDate)
    );

    const feelingData = sortedData.map((e, index) => ({
      id: e.id,
      category: parseDate(e.logDate.slice(0, 10)),
      value: e.endScore,
      sortOrder: index,
      tooltipA: e.rememberToday,
      tooltipB: e.leaveBehind,
    }));

    // Helper to calculate sum and count for tasks
    const getTaskMetrics = (e) => {
      const scores = [
        e.momentOneScore,
        e.momentTwoScore,
        e.momentThreeScore,
        e.momentFourScore,
        e.momentFiveScore,
      ];

      const sum = scores.reduce(
        (acc, score) => acc + (parseFloat(score) || 0),
        0
      );

      // Count valid non-zero scores or strictly 1 ratios (preserving your original logic)
      const count = scores.reduce((acc, score) => {
        const val = parseFloat(score);
        return acc + (val === val || val === 0 ? 1 : 0); // Simplified check based on your logic
      }, 0);

      return { sum, count };
    };

    const taskCompletedData = sortedData.map((e, index) => {
      const { sum } = getTaskMetrics(e);

      let memOne = e.momentOne;

      let memTwo = e.momentTwo;

      let memThree = e.momentThree;

      let memFour = e.momentFour;

      let memFive = e.momentFive;

      return {
        id: e.id,
        category: parseDate(e.logDate.slice(0, 10)),
        value: sum,
        sortOrder: index,
        m1: memOne,
        m1Score: e.momentOneScore,
        m2: memTwo,
        m2Score: e.momentTwoScore,
        m3: memThree,
        m3Score: e.momentThreeScore,
        m4: memFour,
        m4Score: e.momentFourScore,
        m5: memFive,
        m5Score: e.momentFiveScore,
      };
    });

    const taskCompletionRateData = sortedData.map((e, index) => {
      // Note: Recalculating metrics here to match your specific logic for rate
      // You can refactor the logic inside getTaskMetrics to be more robust if needed
      let sum =
        (e.momentOneScore !== undefined && parseFloat(e.momentOneScore)) +
        (e.momentTwoScore !== undefined && parseFloat(e.momentTwoScore)) +
        (e.momentThreeScore !== undefined && parseFloat(e.momentThreeScore)) +
        (e.momentFourScore !== undefined && parseFloat(e.momentFourScore)) +
        (e.momentFiveScore !== undefined && parseFloat(e.momentFiveScore));

      // Replicating your specific count logic exactly:
      let count =
        (e.momentOneScore / e.momentOneScore === 1 || e.momentOneScore == 0
          ? 1
          : 0) +
        (e.momentTwoScore / e.momentTwoScore == 1 || e.momentTwoScore == 0
          ? 1
          : 0) +
        (e.momentThreeScore / e.momentThreeScore === 1 ||
        e.momentThreeScore == 0
          ? 1
          : 0) +
        (e.momentFourScore / e.momentFourScore === 1 || e.momentFourScore == 0
          ? 1
          : 0) +
        (e.momentFiveScore / e.momentFiveScore === 1 || e.momentFiveScore == 0
          ? 1
          : 0);

      let memOne = e.momentOne;

      let memTwo = e.momentTwo;

      let memThree = e.momentThree;

      let memFour = e.momentFour;

      let memFive = e.momentFive;

      return {
        id: e.id,
        category: parseDate(e.logDate.slice(0, 10)),
        value: count === 0 ? 0 : sum / 10 / count, // Prevent NaN
        sortOrder: index,
        m1: memOne,
        m1Score: e.momentOneScore,
        m2: memTwo,
        m2Score: e.momentTwoScore,
        m3: memThree,
        m3Score: e.momentThreeScore,
        m4: memFour,
        m4Score: e.momentFourScore,
        m5: memFive,
        m5Score: e.momentFiveScore,
      };
    });

    const waveData = sortedData.map((e, index) => {
      let sum =
        (e.momentOneScore !== undefined && parseFloat(e.momentOneScore)) +
        (e.momentTwoScore !== undefined && parseFloat(e.momentTwoScore)) +
        (e.momentThreeScore !== undefined && parseFloat(e.momentThreeScore)) +
        (e.momentFourScore !== undefined && parseFloat(e.momentFourScore)) +
        (e.momentFiveScore !== undefined && parseFloat(e.momentFiveScore));

      let count =
        (e.momentOneScore / e.momentOneScore === 1 || e.momentOneScore == 0
          ? 1
          : 0) +
        (e.momentTwoScore / e.momentTwoScore == 1 || e.momentTwoScore == 0
          ? 1
          : 0) +
        (e.momentThreeScore / e.momentThreeScore === 1 ||
        e.momentThreeScore == 0
          ? 1
          : 0) +
        (e.momentFourScore / e.momentFourScore === 1 || e.momentFourScore == 0
          ? 1
          : 0) +
        (e.momentFiveScore / e.momentFiveScore === 1 || e.momentFiveScore == 0
          ? 1
          : 0);

      // Avoid division by zero
      const safeCount = count === 0 ? 1 : count;

      let wave =
        parseFloat(sum / 10) +
        parseFloat((sum / 10 / safeCount) * 5) +
        parseFloat(e.endScore);

      return {
        id: e.id,
        category: parseDate(e.logDate.slice(0, 10)),
        value: wave,
        sortOrder: index,
        tooltipA: e.rememberToday,
        tooltipB: e.leaveBehind,
      };
    });

    const feelingDoingData = sortedData.map((e, index) => {
      const { sum } = getTaskMetrics(e); // This is "Task Completion"

      return {
        id: e.id,
        category: parseDate(e.logDate.slice(0, 10)),
        x: sum, // x-axis: How much you did
        y: parseFloat(e.endScore) || 0, // y-axis: How you felt
        sortOrder: index,

        tooltipA: e.rememberToday,
        tooltipB: e.leaveBehind,
      };
    });

    const efficiencyDoingData = sortedData.map((e, index) => {
      const { sum, count } = getTaskMetrics(e); // This is "Task Completion"

      let memOne = e.momentOne;

      let memTwo = e.momentTwo;

      let memThree = e.momentThree;

      let memFour = e.momentFour;

      let memFive = e.momentFive;

      return {
        id: e.id,
        category: parseDate(e.logDate.slice(0, 10)),
        x: sum, // x-axis: How much you did
        y: sum / (count * 10) || 0, // y-axis: How you felt
        sortOrder: index,
        m1: memOne,
        m1Score: e.momentOneScore,
        m2: memTwo,
        m2Score: e.momentTwoScore,
        m3: memThree,
        m3Score: e.momentThreeScore,
        m4: memFour,
        m4Score: e.momentFourScore,
        m5: memFive,
        m5Score: e.momentFiveScore,
      };
    });

    // --- New regressions ---

    const morningEveningData = sortedData
      .filter(
        (e) =>
          parseFloat(e.startScore) > 0 && parseFloat(e.endScore) > 0
      )
      .map((e, index) => ({
        id: e.id,
        category: parseDate(e.logDate.slice(0, 10)),
        x: parseFloat(e.startScore) || 0,
        y: parseFloat(e.endScore) || 0,
        sortOrder: index,
        tooltipA: e.rememberToday,
        tooltipB: e.leaveBehind,
      }));

    const doingShiftData = sortedData
      .filter(
        (e) =>
          parseFloat(e.startScore) > 0 && parseFloat(e.endScore) > 0
      )
      .map((e, index) => {
        const scores = [
          e.momentOneScore,
          e.momentTwoScore,
          e.momentThreeScore,
          e.momentFourScore,
          e.momentFiveScore,
        ]
          .map((s) => parseFloat(s) || 0)
          .filter((s) => s > 0);
        const avg =
          scores.length > 0
            ? scores.reduce((a, b) => a + b, 0) / scores.length
            : 0;
        return {
          id: e.id,
          category: parseDate(e.logDate.slice(0, 10)),
          x: avg,
          y:
            (parseFloat(e.endScore) || 0) -
            (parseFloat(e.startScore) || 0),
          sortOrder: index,
          tooltipA: e.rememberToday,
          tooltipB: e.leaveBehind,
        };
      });

    // Pair consecutive days (exactly 1 calendar day apart) to study
    // whether yesterday's evening predicts today's morning.
    const carryoverData = [];
    for (let i = 1; i < sortedData.length; i++) {
      const prev = sortedData[i - 1];
      const curr = sortedData[i];
      const prevEnd = parseFloat(prev.endScore);
      const currStart = parseFloat(curr.startScore);
      if (!(prevEnd > 0) || !(currStart > 0)) continue;
      const prevStr = prev.logDate && prev.logDate.slice(0, 10);
      const currStr = curr.logDate && curr.logDate.slice(0, 10);
      if (!prevStr || !currStr) continue;
      // DST-safe: compute "prev + 1 day" in UTC and compare strings.
      const nextOfPrev = new Date(prevStr + "T00:00:00Z");
      nextOfPrev.setUTCDate(nextOfPrev.getUTCDate() + 1);
      if (nextOfPrev.toISOString().slice(0, 10) !== currStr) continue;
      carryoverData.push({
        id: curr.id,
        category: parseDate(currStr),
        x: prevEnd,
        y: currStart,
        sortOrder: carryoverData.length,
        tooltipA: curr.rememberToday,
        tooltipB: curr.leaveBehind,
      });
    }

    // Moment Mix: per-moment aggregation across all days.
    // Bars  -> average score % when moment is logged
    // Line  -> % of journalling days where moment was logged
    const momentLabels = ["One", "Two", "Three", "Four", "Five"];
    const total = sortedData.length;
    const momentMixData = total
      ? momentLabels.map((suffix, i) => {
          const titleKey = `moment${suffix}`;
          const scoreKey = `${titleKey}Score`;
          const logged = sortedData.filter((d) => {
            const hasTitle =
              typeof d[titleKey] === "string" && d[titleKey].trim() !== "";
            const rawScore = d[scoreKey];
            const hasScore =
              rawScore !== null &&
              rawScore !== undefined &&
              rawScore !== "" &&
              !Number.isNaN(parseFloat(rawScore));
            return hasTitle || hasScore;
          });
          const scored = logged
            .map((d) => parseFloat(d[scoreKey]))
            .filter((v) => !Number.isNaN(v));
          const avg = scored.length
            ? scored.reduce((a, b) => a + b, 0) / scored.length
            : 0;
          const avgScorePct = avg / 10;
          const planFreqPct = logged.length / total;
          return {
            category: `Moment ${i + 1}`,
            momentIndex: i + 1,
            avgScorePct,
            planFreqPct,
            loggedCount: logged.length,
            totalDays: total,
            value: avgScorePct,
          };
        })
      : [];

    return {
      feelingData,
      taskCompletedData,
      taskCompletionRateData,
      waveData,
      feelingDoingData,
      efficiencyDoingData,
      morningEveningData,
      doingShiftData,
      carryoverData,
      momentMixData,
    };
  }, [data]);

  // Memoised because it lands in the charts' d3 effect dependencies. Rebuilding
  // it every render would redraw the whole plot on each hover.
  const selectedConfig = useMemo(() => {
    switch (graphSelected) {
      case "feeling_score":
        return {
          ...feeling_config,
          graphData: processedData.feelingData,
        };
      case "tasks_completed":
        return {
          ...task_completion_config,
          graphData: processedData.taskCompletedData,
        };
      case "tasks_completion_rate":
        return {
          ...task_completion_rate_config,
          graphData: processedData.taskCompletionRateData,
        };
      case "wave_score":
        return {
          ...wave_config,
          graphData: processedData.waveData,
        };
      case "feeling_doing":
        return {
          ...feeling_doing_config,
          graphData: processedData.feelingDoingData,
        };
      case "morning_evening":
        return {
          ...morning_evening_config,
          graphData: processedData.morningEveningData,
        };
      case "doing_shift":
        return {
          ...doing_shift_config,
          graphData: processedData.doingShiftData,
        };
      case "carryover":
        return {
          ...carryover_config,
          graphData: processedData.carryoverData,
        };
      case "moment_mix":
        return {
          ...moment_mix_config,
          graphData: processedData.momentMixData,
        };
      default:
        return null;
    }
  }, [graphSelected, processedData]);

  if (!selectedConfig) {
    return <p>Select a graph to view.</p>;
  }

  return (
    <>
      <ChartHeader
        title={title}
        config={selectedConfig}
        family={family}
        average={average}
        picker={picker}
      />
      <GraphWrapper
        family={family}
        viewMode={viewMode}
        config={selectedConfig}
        data={data}
        onNodeSelect={onNodeSelect}
        onNodeHover={onNodeHover}
        onViewStateChange={onViewStateChange}
        viewOffset={viewOffset}
        setViewOffset={setViewOffset}
        currentGraphId={graphSelected}
        selectedId={selectedId}
      />
    </>
  );
}

export default GraphRenderer;
